import { useParams } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import ProductsTable from "./ProductsTable";
import axios from "axios";
import Spinner from "../../components/Spinner";
import { useAuth } from "../../contexts/AuthContext";

export default function AdminCategoryProducts() {
  const { categoryId } = useParams();
  const { auth } = useAuth();

  const token = auth?.token;

  const [products, setProducts] = useState([]);
  const [categoryName, setCategoryName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const PRODUCT_API = process.env.REACT_APP_PRODUCT_SERVICE_URL;

  const authHeader = useCallback(() => {
    if (!token) {
      throw new Error("Missing authentication token");
    }

    return {
      Authorization: `Bearer ${token}`,
    };
  }, [token]);

  const fetchProducts = useCallback(async () => {
    if (!token) {
      setLoading(false);
      setError("Authentication required");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const res = await axios.get(`${PRODUCT_API}/products/admin`, {
        params: {
          categoryId,
          page: 0,
          size: 50,
        },
        headers: authHeader(),
      });

      const content = res.data?.content || [];

      setProducts(content);
      setCategoryName(content[0]?.category?.name || "Category");
    } catch (err) {
      console.error("Failed to load products", err);

      setError(err?.response?.data?.message || "Failed to load products");
    } finally {
      setLoading(false);
    }
  }, [categoryId, PRODUCT_API, token, authHeader]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  /* ---------------- APPROVE ---------------- */

  const handleApprove = async (id) => {
    if (!token) {
      setError("Authentication required");
      return;
    }

    try {
      await axios.put(
        `${PRODUCT_API}/products/${id}/moderate`,
        {
          status: "APPROVED",
          reason: null,
        },
        {
          headers: authHeader(),
        },
      );

      await fetchProducts();
    } catch (err) {
      console.error("Approve failed", err);

      setError(err?.response?.data?.message || "Failed to approve product");
    }
  };

  /* ---------------- REJECT ---------------- */

  const handleReject = async (id, reason) => {
    if (!token) {
      setError("Authentication required");
      return;
    }

    try {
      await axios.put(
        `${PRODUCT_API}/products/${id}/moderate`,
        {
          status: "REJECTED",
          reason,
        },
        {
          headers: authHeader(),
        },
      );

      await fetchProducts();
    } catch (err) {
      console.error("Reject failed", err);

      setError(err?.response?.data?.message || "Failed to reject product");
    }
  };

  /* ---------------- LOADING ---------------- */

  if (loading) {
    return <Spinner />;
  }

  /* ---------------- ERROR ---------------- */

  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }

  return (
    <div>
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="mb-1">{categoryName} Products</h3>

          <div className="text-muted">Moderation panel</div>
        </div>

        <span className="badge bg-primary">{products.length} Products</span>
      </div>

      {/* Products Table */}
      <ProductsTable
        products={products}
        onApprove={handleApprove}
        onReject={handleReject}
      />
    </div>
  );
}
